import { useEffect, useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useMessage, useProfileIntegrationsList } from '@pepper/ui'
import { ContentRefreshDomain } from '@pepper/utils/src/types/domain'
import axios from '@pepper/utils/src/function/axiosInstance'
import extract from '@pepper/utils/src/function/extract'

import { SEO_SERVICE_BASE_URL } from '@pepper/utils/src/api/config'

type NormalizedDomains = Record<string, ContentRefreshDomain>
type ReturnType = {
  data: ContentRefreshDomain[]
  normalizedDomains: NormalizedDomains
  integrationsData: IIntegrationProps | undefined
  refetchIntegrationsData: () => void
  isGoogleConnected: boolean
  isLoading: boolean
  isFetching: boolean
  connectedGoogleAccount?: IIntegrationAccount
  refetch: () => void
  error: Error | null
}

const STALE_TIME_MS = 300000 // 5mins
const MILLISECONDS_PER_SECOND = 1000
const MAX_RETRY_DELAY = 30
const REFETCH_INTERVAL = 2000

export const fetchContentRefreshDomains = async ({
  companyId
}: {
  companyId: string
}): Promise<ApiResponseV2<ContentRefreshDomain[]>> => {
  try {
    const response = await axios.get(
      `${SEO_SERVICE_BASE_URL}/companies/${companyId}/domains`
    )
    return extract.data(response)
  } catch (error) {
    return extract.error(error)
  }
}

export const useContentRefreshDomains = (
  companyId?: string,
  refetchOnInterval = false
): ReturnType => {
  const { showError } = useMessage()
  const { data: integrationsData, refetch: refetchIntegrationsData } =
    useProfileIntegrationsList({
      companyId: companyId!,
      enabled: !!companyId
    })

  const handleApiRequest = async () => {
    const response = await fetchContentRefreshDomains({
      companyId: companyId!
    })
    if (response.status === 'error') {
      throw new Error(response.error)
    }
    return response?.data
  }

  const {
    isLoading,
    isFetching,
    isError,
    error,
    refetch,
    data = []
  } = useQuery<ContentRefreshDomain[], Error>({
    queryKey: ['content-refresh-domains', companyId, refetchOnInterval],
    queryFn: handleApiRequest,
    enabled: !!companyId && !!integrationsData,
    retry: 5,
    retryDelay: attempt =>
      Math.min(
        attempt > 1
          ? 2 ** attempt * MILLISECONDS_PER_SECOND
          : MILLISECONDS_PER_SECOND,
        MAX_RETRY_DELAY * MILLISECONDS_PER_SECOND
      ),
    staleTime: STALE_TIME_MS,
    cacheTime: STALE_TIME_MS,
    refetchInterval: refetchOnInterval ? REFETCH_INTERVAL : false
  })

  const normalizedDomains: NormalizedDomains = useMemo(
    () =>
      data.reduce(
        (res, domain) => ({
          ...res,
          [domain.id]: domain
        }),
        {}
      ),
    [data.length]
  )

  const isGoogleConnected = !!integrationsData?.records.find(
    r => r.integration.name === 'Google'
  )?.accounts.length

  const connectedGoogleAccount = integrationsData?.records.find(
    r => r.integration.name === 'Google'
  )?.accounts?.[0]

  useEffect(() => {
    if (isError) {
      showError(error?.message)
    }
  }, [isError, error, showError])

  return {
    data,
    normalizedDomains,
    integrationsData,
    refetchIntegrationsData,
    isGoogleConnected,
    connectedGoogleAccount,
    isLoading,
    isFetching,
    refetch,
    error
  }
}
